import React, { useState, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';
import { Button } from '../components/ui/button';
import { Save, Edit, Eye, Download, Code, Play, Copy } from 'lucide-react';

const CodeViewer = ({ file, content, projectId, myProfile, onSave, onDownload, isLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [codeContent, setCodeContent] = useState(content || '');
  const [originalContent, setOriginalContent] = useState(content || '');
  const [language, setLanguage] = useState('plaintext');

  useEffect(() => {
    if (content) {
      setCodeContent(content);
      setOriginalContent(content);
      detectLanguage();
    }
  }, [content]);

  const detectLanguage = () => {
    const extension = file.original_filename.split('.').pop()?.toLowerCase();
    const languageMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'cs': 'csharp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'swift': 'swift',
      'kt': 'kotlin',
      'scala': 'scala',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'sass': 'sass',
      'less': 'less',
      'json': 'json',
      'xml': 'xml',
      'yaml': 'yaml',
      'yml': 'yaml',
      'md': 'markdown',
      'sql': 'sql',
      'sh': 'shell',
      'bash': 'shell',
      'dockerfile': 'dockerfile',
      'r': 'r',
      'matlab': 'matlab',
      'm': 'matlab'
    };
    
    setLanguage(languageMap[extension] || 'plaintext');
  };

  const handleSave = async () => {
    await onSave(codeContent);
    setOriginalContent(codeContent);
    setIsEditing(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(codeContent);
      // You could add a toast notification here
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  const getLanguageIcon = () => {
    const iconMap = {
      'javascript': '🟨',
      'typescript': '🔷',
      'python': '🐍',
      'java': '☕',
      'html': '🌐',
      'css': '🎨',
      'json': '📋',
      'markdown': '📝'
    };
    return iconMap[language] || '📄';
  };

  const hasChanges = codeContent !== originalContent;
  const lineCount = codeContent.split('\n').length;

  const editorOptions = {
    selectOnLineNumbers: true,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    minimap: { enabled: lineCount > 50 },
    wordWrap: 'on',
    fontSize: 14,
    lineHeight: 21,
    fontFamily: "'Fira Code', 'JetBrains Mono', 'Monaco', 'Menlo', monospace",
    theme: 'vs',
    readOnly: !isEditing
  };

  return (
    <div className="space-y-4">
      {/* Code File Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Code className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              {getLanguageIcon()} {file.original_filename}
            </h3>
            <p className="text-sm text-gray-500">
              {language.toUpperCase()} • {lineCount} lines • {Math.round(file.file_size / 1024)} KB
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={copyToClipboard}
          >
            <Copy className="h-4 w-4 mr-1" />
            Copy
          </Button>
          
          {!isEditing ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          ) : (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setCodeContent(originalContent);
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                size="sm"
                onClick={handleSave}
                disabled={isLoading || !hasChanges}
                className="bg-cyan-500 hover:bg-cyan-600"
              >
                <Save className="h-4 w-4 mr-1" />
                Save Code
              </Button>
            </>
          )}
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={onDownload}
          >
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>

      {/* Code Editor */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b text-sm">
          <div className="flex items-center gap-4 text-gray-600">
            <span>Language: <span className="font-medium">{language.toUpperCase()}</span></span>
            <span>Lines: <span className="font-medium">{lineCount}</span></span>
            <span>Characters: <span className="font-medium">{codeContent.length}</span></span>
          </div>
          <div>
            {isEditing ? (
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <span className="text-orange-600 font-medium">● Unsaved changes</span>
                )}
                <span className="text-orange-600">● Editing</span>
              </div>
            ) : (
              <span className="text-green-600">● Read-only</span>
            )}
          </div>
        </div>
        
        <div style={{ height: '500px' }}>
          <Editor
            language={language}
            value={codeContent}
            onChange={(value) => setCodeContent(value || '')}
            options={editorOptions}
            loading={
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-3"></div>
                <span className="text-gray-600">Loading editor...</span>
              </div>
            }
          />
        </div>
      </div>

      {/* Code Statistics and Actions */}
      <div className="flex items-center justify-between text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-4">
          <span>File type: {language.toUpperCase()}</span>
          <span>Size: {(codeContent.length / 1024).toFixed(1)} KB</span>
          <span>Words: {codeContent.split(/\s+/).filter(word => word.length > 0).length}</span>
        </div>
        <div className="flex items-center gap-2">
          {language === 'javascript' || language === 'typescript' || language === 'python' ? (
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700">
              <Play className="h-3 w-3 mr-1" />
              Run (Coming Soon)
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CodeViewer;